import type { InternalAxiosRequestConfig } from "axios";
import { myLocalStorage } from "../components/StorageHelper";
import {
  checkIfTokenExpired,
  decodeKeycloakToken,
  refreshAccessToken,
  sendTokenToBackend,
} from "./tokenHelpers";
import { logOut } from "../services/Auth";
import { clearLocalStorageOnLogout } from "../services/Helpers";

/** Unix seconds — refresh when now >= this value (token exp minus 2 minutes). */
export const ACCESS_TOKEN_REFRESH_AT_KEY = "access_token_refresh_at";

const REFRESH_BUFFER_SECONDS = 120;
const CHECK_INTERVAL_MS = 15_000;
const REFRESH_TIMEOUT_MS = 15_000;
const AUTH_PAGES = ["/", "/signIn", "/signUp"];

type RefreshOptions = {
  /** Proactive refresh can cancel in-flight API calls; 401 refresh does not. */
  cancelPending?: boolean;
  /** Only logout when refresh token is actually expired/missing. */
  logoutOnFailure?: boolean;
};

let checkIntervalId: ReturnType<typeof setInterval> | null = null;
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const pendingAbortControllers = new Set<AbortController>();

function isAuthPage(): boolean {
  return AUTH_PAGES.includes(window.location.pathname);
}

function processQueue(error: unknown | null, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token!);
  });
  failedQueue = [];
}

function updateRefreshAtFromAccessToken(accessToken: string): void {
  const decoded = decodeKeycloakToken(accessToken);
  if (decoded?.exp) {
    myLocalStorage.setItem(
      ACCESS_TOKEN_REFRESH_AT_KEY,
      String(decoded.exp - REFRESH_BUFFER_SECONDS),
    );
  }
}

function shouldRefreshNow(): boolean {
  const accessToken = myLocalStorage.getItem("access_token");
  if (!accessToken) return false;

  let stored = myLocalStorage.getItem(ACCESS_TOKEN_REFRESH_AT_KEY);
  if (!stored) {
    updateRefreshAtFromAccessToken(accessToken);
    stored = myLocalStorage.getItem(ACCESS_TOKEN_REFRESH_AT_KEY);
    // Decode failed / no exp — do not recurse; skip until next valid register.
    if (!stored) return false;
  }

  const refreshAt = Number(stored);
  if (Number.isNaN(refreshAt)) return false;

  return Math.floor(Date.now() / 1000) >= refreshAt;
}

function cancelPendingRequests(): void {
  pendingAbortControllers.forEach((controller) => {
    try {
      controller.abort();
    } catch {
      // ignore
    }
  });
  pendingAbortControllers.clear();
}

export function trackRequestAbortController(controller: AbortController): void {
  pendingAbortControllers.add(controller);
  controller.signal.addEventListener(
    "abort",
    () => pendingAbortControllers.delete(controller),
    { once: true },
  );
}

export function attachAbortTracking(
  config: InternalAxiosRequestConfig,
): InternalAxiosRequestConfig {
  if (config.signal) return config;

  const controller = new AbortController();
  trackRequestAbortController(controller);
  config.signal = controller.signal;
  return config;
}

async function logOutDueToTokenExpire(): Promise<void> {
  stopSessionTokenWatcher();
  const email = myLocalStorage.getItem("email");
  try {
    await logOut(email || "");
  } catch (err) {
    console.error("Logout failed:", err);
  }
  clearLocalStorageOnLogout();
}

function isRefreshTokenInvalid(): boolean {
  const refreshToken = myLocalStorage.getItem("refresh_token");
  return !refreshToken || checkIfTokenExpired(refreshToken);
}

async function logoutIfSessionInvalid(): Promise<void> {
  if (!isAuthPage() && isRefreshTokenInvalid()) {
    await logOutDueToTokenExpire();
  }
}

/** Call after login or whenever access/refresh tokens are saved. */
export function registerSessionTokens(
  accessToken: string,
  refreshToken?: string | null,
): void {
  myLocalStorage.setItem("access_token", accessToken);
  if (refreshToken) {
    myLocalStorage.setItem("refresh_token", refreshToken);
  }
  updateRefreshAtFromAccessToken(accessToken);
  startSessionTokenWatcher();
}

export function isSessionRefreshing(): boolean {
  return isRefreshing;
}

export async function handleInvalidSessionLogout(): Promise<void> {
  await logoutIfSessionInvalid();
}

export function enqueueRefreshWaiter(
  resolve: (token: string) => void,
  reject: (error: unknown) => void,
): void {
  failedQueue.push({ resolve, reject });
}

/**
 * Shared refresh used by apiClient (401) and the 15s proactive watcher.
 */
export async function refreshSessionTokens(
  options: RefreshOptions = {},
): Promise<string> {
  const { cancelPending = false, logoutOnFailure = false } = options;

  if (refreshPromise) {
    return refreshPromise;
  }

  if (isRefreshTokenInvalid()) {
    if (logoutOnFailure) {
      await logoutIfSessionInvalid();
    }
    throw new Error("Refresh token expired");
  }

  if (cancelPending) {
    cancelPendingRequests();
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const realm = myLocalStorage.getItem("realm");
      const email = myLocalStorage.getItem("email");
      let role = myLocalStorage.getItem("role");
      if (myLocalStorage.getItem("azuresso")) role = "TENANT_USER";

      const { access_token, refresh_token } = await Promise.race([
        refreshAccessToken(),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("Token refresh timed out")), REFRESH_TIMEOUT_MS),
        ),
      ]);

      if (!access_token) {
        throw new Error("Token refresh failed");
      }

      myLocalStorage.setItem("access_token", access_token);
      myLocalStorage.setItem("refresh_token", refresh_token);
      updateRefreshAtFromAccessToken(access_token);

      const decode = decodeKeycloakToken(access_token);
      await sendTokenToBackend(realm, email, role, access_token, decode?.exp);

      processQueue(null, access_token);
      return access_token;
    } catch (error) {
      processQueue(error, null);
      // Only logout when refresh token is truly invalid — not on timeout/network blips.
      if (logoutOnFailure) {
        await logoutIfSessionInvalid();
      }
      throw error;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function checkAndRefreshIfNeeded(): Promise<void> {
  if (isAuthPage()) return;
  if (!myLocalStorage.getItem("access_token")) return;
  if (!shouldRefreshNow()) return;
  if (isRefreshing) return;

  try {
    await refreshSessionTokens({ cancelPending: false, logoutOnFailure: false });
  } catch (error) {
    console.warn("Scheduled token refresh failed, will retry:", error);
  }
}

function handleVisibilityChange(): void {
  if (document.visibilityState === "visible") {
    void checkAndRefreshIfNeeded();
  }
}

export function startSessionTokenWatcher(): void {
  if (checkIntervalId) return;

  const token = myLocalStorage.getItem("access_token");
  if (token) {
    updateRefreshAtFromAccessToken(token);
  }

  void checkAndRefreshIfNeeded();

  checkIntervalId = setInterval(() => {
    void checkAndRefreshIfNeeded();
  }, CHECK_INTERVAL_MS);

  document.addEventListener("visibilitychange", handleVisibilityChange);
}

export function stopSessionTokenWatcher(): void {
  if (checkIntervalId) {
    clearInterval(checkIntervalId);
    checkIntervalId = null;
  }
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  cancelPendingRequests();
  myLocalStorage.removeItem(ACCESS_TOKEN_REFRESH_AT_KEY);
}

export async function waitForActiveRefresh(): Promise<void> {
  if (!refreshPromise) return;
  try {
    // Never block API traffic indefinitely if refresh stalls (common under load).
    await Promise.race([
      refreshPromise,
      new Promise<void>((resolve) =>
        setTimeout(resolve, REFRESH_TIMEOUT_MS + 2_000),
      ),
    ]);
  } catch {
    // caller handles auth errors via 401 path
  }
}
